import { extractApiErrorMessage } from '../../utils/errorHandler';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { d5m4Api, ReverseCandidate } from '../../services/d5m4Api';
import {
  BatchAcceptanceBridge,
  pwd5m4f1AcceptanceManifest,
} from '../../features/acceptance';
import type { AcceptanceManifest } from '../../features/acceptance';
import './D5M4Form.css';
const acceptanceActionIds = {
  list: 'PWD5N4F1-ACT-QUERY',
  writeoff: 'PWD5N4F1-ACT-REVERSE',
};
void acceptanceActionIds;

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
interface D5M4FormProps {
  api?: typeof d5m4Api;
  manifest?: AcceptanceManifest;
}

const D5M4Form: React.FC<D5M4FormProps> = ({
  api = d5m4Api,
  manifest = pwd5m4f1AcceptanceManifest,
}) => {
  const activeManifest = manifest;
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await api.queryCandidates(startNo, endNo);
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的託工入庫單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      message.error(extractApiErrorMessage(e, '查詢失敗'));
    } finally {
      setLoading(false);
    }
  }, [api, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fd140_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的託工入庫單');
      return;
    }
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆託工入庫單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fd140_01);
        setExecuting(true);
        try {
      const res = await api.reverse(selectedNos);
          setLastApiResponse(res);
          if (res.failed === 0) {
            message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          message.error(extractApiErrorMessage(e, '沖銷執行失敗'));
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [api, selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fd140_01, e.target.checked)} data-contract-field="fd140_01"
        />
      ),
    },
    {
      title: <span data-contract-field="fd140_01">入庫單號</span>,
      dataIndex: ['item', 'fd140_01'],
      key: 'fd140_01',
      width: 120,
    },
    {
      title: <span data-contract-field="fd140_12">廠商代碼</span>,
      dataIndex: ['item', 'fd140_12'],
      key: 'fd140_12',
      width: 110,
    },
    {
      title: <span data-contract-field="fd140_13">廠商名稱</span>,
      dataIndex: ['item', 'fd140_13'],
      key: 'fd140_13',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fd140_11">入庫日期</span>,
      dataIndex: ['item', 'fd140_11'],
      key: 'fd140_11',
      width: 110,
    },
    {
      title: <span data-contract-field="fd140_16">金額</span>,
      dataIndex: ['item', 'fd140_16'],
      key: 'fd140_16',
      width: 110,
      align: 'right',
    },
    {
      title: <span data-contract-field="fd140_37">工繳係數</span>,
      dataIndex: ['item', 'fd140_37'],
      key: 'fd140_37',
      width: 100,
      align: 'right',
    },
    {
      title: <span data-contract-field="ui_product_no">產品代號</span>,
      key: 'product_no',
      width: 120,
      render: (_: unknown, record: ItemState) =>
        `${record.item.fd140_181}-${record.item.fd140_182}`.replace(/^-$/, ''),
    },
  ];

  // ---- Render ----
  return (
    <div className="d5m4-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                採購管理
              </a>
              <span>/</span>
              <span className="current">託工入庫單沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>託工入庫單沖銷</h1>
            <span className="badge">D5M4</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="query_receipt_start">入庫單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <label className="separator" data-contract-field="query_receipt_end">至</label>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <span data-acceptance-action="PWD5M4-ACT-QUERY">
          <Button
            type="primary"
            onClick={onQuery}
            loading={loading}
            data-acceptance-action="PWD5N4F1-ACT-QUERY"
            data-acceptance-reveal-fields
          >
            確定範圍
          </Button>
          </span>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          <Table<ItemState>
            columns={columns}
            dataSource={items}
            rowKey={(record) => record.item.fd140_01}
            pagination={false}
            size="small"
            scroll={{ y: 420 }}
            locale={{ emptyText: queried ? '查無資料' : '請輸入入庫單號範圍並點擊「確定範圍」查詢' }}
          />
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <span data-acceptance-action="PWD5M4-ACT-REVERSE">
          <Button
            type="primary"
            data-acceptance-action="PWD5N4F1-ACT-REVERSE"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          </span>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={activeManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fd140_01)}
          keyOf={(item) => item.fd140_01}
          queryExact={async (key) => (await api.queryCandidates(key, key)).items?.[0] || null}
          lastApiResponse={lastApiResponse}
          loading={loading || executing}
          onAutoFill={() => setItems(prev => prev.map((state, index) => ({ ...state, selected: index === 0 })))}
          autoFillDisabled={items.length === 0}
          autoFillReason="只選取正式 API 回傳的第一筆託工入庫退出候選。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default D5M4Form;
